import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { BookOpen, RefreshCw, AlertTriangle, CheckCircle2, Calendar, Clock } from "lucide-react";
import { circulationApi, type CheckoutRecord, type PaginatedResponse } from "@/lib/api/circulation";

const statusConfig: Record<string, { label: string; cls: string }> = {
  ACTIVE: { label: "Active", cls: "bg-blue-100 text-blue-700" },
  OVERDUE: { label: "Overdue", cls: "bg-red-100 text-red-600" },
  RETURNED: { label: "Returned", cls: "bg-green-100 text-green-700" },
  LOST: { label: "Lost", cls: "bg-gray-100 text-gray-600" },
};

type TabFilter = "current" | "history";

const MyBooksPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<CheckoutRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabFilter>("current");
  const [page, setPage] = useState(1);
  const [renewingId, setRenewingId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) loadCheckouts();
  }, [user?.id, tab, page]);

  const loadCheckouts = async () => {
    setLoading(true);
    try {
      const status = tab === "current" ? undefined : "RETURNED";
      const result = await circulationApi.getCheckouts({
        userId: user?.id,
        status,
        page,
        limit: 20,
      });

      if (tab === "current") {
        result.data = result.data.filter((c) => c.status === "ACTIVE" || c.status === "OVERDUE");
        result.total = result.data.length;
      }

      setData(result);
    } catch {
      toast.error("Failed to load your books");
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async (checkoutId: string) => {
    setRenewingId(checkoutId);
    try {
      await circulationApi.renew(checkoutId);
      toast.success("Renewed successfully");
      loadCheckouts();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Renewal failed");
    } finally {
      setRenewingId(null);
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const currentItems = data?.data ?? [];

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="My Books"
          description={tab === "current" ? "Currently checked out items" : "Borrowing history"}
          breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "My Books" }]}
          showSearch={false}
          titleSize="md"
        />

        {/* Tabs */}
        <div className="flex bg-muted/50 rounded-lg p-1 gap-1 w-fit">
          {(["current", "history"] as TabFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
              }}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                tab === t
                  ? "bg-background shadow font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "current" ? "Currently Borrowed" : "History"}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : currentItems.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">
              {tab === "current" ? "No books checked out" : "No borrowing history yet"}
            </p>
            <p className="text-sm mt-1">
              {tab === "current"
                ? "Visit the library to borrow books."
                : "Your completed checkouts will appear here."}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {currentItems.map((co) => {
              const daysLeft = getDaysUntilDue(co.dueDate);
              const isOverdue = co.status === "OVERDUE" || daysLeft < 0;
              const isDueSoon = !isOverdue && daysLeft <= 3;
              const cfg = statusConfig[co.status] ?? statusConfig.ACTIVE;

              return (
                <Card key={co.id} className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Cover placeholder */}
                    <div className="w-12 h-16 bg-muted/50 rounded flex items-center justify-center shrink-0">
                      <BookOpen className="w-5 h-5 text-muted-foreground" />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm line-clamp-2">
                        {co.item?.biblio?.title ?? co.book?.title ?? "Unknown Title"}
                      </h3>
                      {co.item?.biblio?.authors && co.item.biblio.authors.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {co.item.biblio.authors.join(", ")}
                        </p>
                      )}

                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <Badge variant="secondary" className={`${cfg.cls} text-xs`}>
                          {cfg.label}
                        </Badge>

                        {tab === "current" && (
                          <div
                            className={`flex items-center gap-1 text-xs ${
                              isOverdue
                                ? "text-red-600 font-semibold"
                                : isDueSoon
                                  ? "text-amber-600 font-medium"
                                  : "text-muted-foreground"
                            }`}
                          >
                            <Calendar className="w-3 h-3" />
                            {isOverdue
                              ? `Overdue by ${Math.abs(daysLeft)} day(s)`
                              : daysLeft === 0
                                ? "Due today"
                                : `Due in ${daysLeft} day(s)`}
                          </div>
                        )}

                        {co.renewalCount > 0 && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            Renewed {co.renewalCount}x
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                        <span>Due: {new Date(co.dueDate).toLocaleDateString()}</span>
                        {co.checkoutBranch && <span>{co.checkoutBranch.name}</span>}
                        {co.returnDate && (
                          <span>Returned: {new Date(co.returnDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    {/* Renew action (only for current items) */}
                    {tab === "current" && (co.status === "ACTIVE" || co.status === "OVERDUE") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => handleRenew(co.id)}
                        disabled={renewingId === co.id}
                      >
                        {renewingId === co.id ? (
                          <LoadingSpinner />
                        ) : (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Renew
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default MyBooksPage;
